import {
  ConsultaInventarioSchema,
  FILTROS_DE_COLUMNA_INVENTARIO,
  type BigBagResumen,
  type ConfiguracionDeVista,
  type ConsultaInventario,
  type OrdenInventario,
  type TotalesDeBigBags,
} from '@cnc/contracts';
import {useCallback, useState} from 'react';
import {Link, useSearchParams} from 'react-router';
import {DescargaEnFormatoBase} from '../componentes/descarga-formato-base';
import {
  consultaDesdeParametros,
  parametrosDesdeConsulta,
  soloFiltros,
  useEliminarBigBags,
  useFiltrosDeInventario,
  useInventario,
  useOpcionesDeColumnaDeInventario,
} from '../api/inventario';
import {FiltrosPorColumna} from '../componentes/filtros-por-columna';
import {columnasConFiltro, type UseOpcionesDeColumna} from '../utilidades/columnas-con-filtro';
import {ErrorDeApi} from '../api/cliente';
import {BarraDeVistas} from '../componentes/barra-de-vistas';
import {BotonDeEliminar} from '../componentes/boton-de-eliminar';
import {CambioDeExportacion} from '../componentes/cambio-de-exportacion';
import {CambioEnLoteDeBigBags} from '../componentes/cambio-en-lote-big-bags';
import {OpcionesDeBigBags} from '../componentes/celda-big-bag';
import {ChipsDeTotales} from '../componentes/chips-de-totales';
import {chipsDeTenor} from '../utilidades/tenor';
import {CargarConsolidado} from '../componentes/cargar-consolidado';
import {COLUMNAS_DE_INVENTARIO, DEFINICION_INVENTARIO} from '../componentes/columnas-inventario';
import {mensajeDeBigBags, revisionDeBigBags} from '../componentes/eliminacion';
import {MenuDeHerramientas, OPCION_DE_MENU} from '../componentes/menu-de-herramientas';
import {FiltrosInventario} from '../componentes/filtros-inventario';
import {Paginacion} from '../componentes/paginacion';
import {EncabezadoInventario} from '../componentes/encabezado-inventario';
import {SelectorDeColumnas} from '../componentes/selector-de-columnas';
import {usePermisos} from '../componentes/sesion-contexto';
import {TablaInventario} from '../componentes/tabla-inventario';
import {useColumnas} from '../componentes/usar-columnas';
import {ZonaAmpliable} from '../componentes/zona-ampliable';
import {tr} from '../i18n/idioma';
import {numero} from '../utilidades/formato';

// Sin ningun filtro en la direccion, la tabla abre solo con las bolsas
// disponibles (decision de Daniel, 05/10/2026; antes disponibles y asignadas).
// No se escribe en la direccion para que la vista guardada predeterminada se
// siga abriendo sola; al primer cambio de filtro queda escrito. "Todos" = estado=todos.
const conEstadoInicial = (parametros: URLSearchParams): ConsultaInventario => {
  const consulta = consultaDesdeParametros(parametros);
  return [...parametros.keys()].length === 0 ? {...consulta, estado: 'available'} : consulta;
};

export function PaginaInventario() {
  const [parametros, setParametros] = useSearchParams();
  const consulta = conEstadoInicial(parametros);
  const [columnas, cambiarColumnas] = useColumnas(DEFINICION_INVENTARIO);
  const permisos = usePermisos();
  const inventario = useInventario(consulta);
  // Las listas de los filtros se encadenan: cada una muestra lo que existe con los demas puestos.
  const filtros = useFiltrosDeInventario(soloFiltros(consulta));
  const eliminar = useEliminarBigBags();
  // Valores de una columna para su filtro (se piden al abrir la lista).
  const useOpciones: UseOpcionesDeColumna = (columna, contiene, habilitado) =>
    useOpcionesDeColumnaDeInventario(soloFiltros(consulta), columna, contiene, habilitado);
  // Seleccion por identificador con su numero de BB; sobrevive al cambio de pagina.
  const [seleccion, setSeleccion] = useState<Map<number, string>>(new Map());
  const [consolidado, setConsolidado] = useState(false);
  // Edicion rapida en la tabla (6/10/2026): clic en una celda, escribir y Enter.
  const [edicionRapida, setEdicionRapida] = useState(false);
  const alternarSeleccion = (filas: BigBagResumen[], seleccionar: boolean) =>
    setSeleccion(actual => {
      const nueva = new Map(actual);
      for (const fila of filas) {
        if (seleccionar) nueva.set(fila.id, fila.bbNo);
        else nueva.delete(fila.id);
      }
      return nueva;
    });

  const cambiar = useCallback(
    (cambios: Partial<ConsultaInventario>, conservarPagina = false) => {
      const actual = conEstadoInicial(new URLSearchParams(window.location.search));
      const nueva = {...actual, ...cambios, pagina: conservarPagina ? (cambios.pagina ?? actual.pagina) : 1};
      setParametros(parametrosDesdeConsulta(nueva), {replace: true});
    },
    [setParametros],
  );

  // Abrir una vista guardada: sus filtros y orden van a la direccion y sus columnas a la tabla.
  const aplicarVista = useCallback(
    (configuracion: ConfiguracionDeVista) => {
      setParametros(new URLSearchParams(configuracion.parametros), {replace: true});
      if (configuracion.columnas) cambiarColumnas(configuracion.columnas);
    },
    [setParametros, cambiarColumnas],
  );

  const ordenar = (orden: OrdenInventario) => {
    const direccion = consulta.orden === orden && consulta.direccion === 'asc' ? 'desc' : 'asc';
    cambiar({orden, direccion}, true);
  };

  return (
    <section className="flex h-full flex-col gap-4">
      <EncabezadoInventario
        descripcion={tr('Big Bags en bodega y exportados.', 'Big Bags in the warehouse and exported.')}
        herramientas={
          /* Descargar, cargar, actualizar y columnas en una sola lista (Daniel, 8/10/2026). */
          <MenuDeHerramientas>
            {cerrar => (
              <>
                <DescargaEnFormatoBase
                  informe="consolidated"
                  cadena={parametrosDesdeConsulta(consulta).toString()}
                  claseDelBoton={OPCION_DE_MENU}
                />
                {permisos.editar && (
                  <button
                    type="button"
                    onClick={() => {
                      setConsolidado(x => !x);
                      cerrar();
                    }}
                    className={OPCION_DE_MENU}
                  >
                    {tr('⇪ Cargar Consolidated', '⇪ Upload Consolidated')}
                  </button>
                )}
                {permisos.editar && (
                  <Link to="/inventario/cargas" className={OPCION_DE_MENU}>
                    {tr('＋ Cargar control de empaque', '＋ Upload packing control')}
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => {
                    void inventario.refetch();
                    cerrar();
                  }}
                  className={OPCION_DE_MENU}
                >
                  {tr('↻ Actualizar', '↻ Refresh')}
                </button>
                <SelectorDeColumnas
                  definicion={DEFINICION_INVENTARIO}
                  visibles={columnas}
                  onCambiar={cambiarColumnas}
                  claseDelBoton={OPCION_DE_MENU}
                />
              </>
            )}
          </MenuDeHerramientas>
        }
      />
      {consolidado && permisos.editar && <CargarConsolidado onCerrar={() => setConsolidado(false)} />}

      <BarraDeVistas
        pantalla="inventario"
        actual={{parametros: parametros.toString(), columnas, extra: {}}}
        onAplicar={aplicarVista}
      />

      <ZonaAmpliable titulo={tr('Inventario', 'Inventory')}>
        <FiltrosInventario
          consulta={consulta}
          filtros={filtros.data}
          onCambiar={cambiar}
          onLimpiar={() =>
            setParametros(parametrosDesdeConsulta(ConsultaInventarioSchema.parse({porPagina: consulta.porPagina})), {
              replace: true,
            })
          }
          porColumna={
            <FiltrosPorColumna
              columnas={columnasConFiltro(
                columnas,
                COLUMNAS_DE_INVENTARIO,
                FILTROS_DE_COLUMNA_INVENTARIO,
                consulta.porColumna,
              )}
              valor={consulta.porColumna}
              onCambiar={porColumna => cambiar({porColumna})}
              useOpciones={useOpciones}
            />
          }
        />

        {inventario.isError && (
          <div className="rounded-lg border border-alerta/30 bg-red-50 p-4 text-sm">
            <div className="font-medium text-alerta">
              {tr('No se pudo consultar el inventario', 'Could not load the inventory')}
            </div>
            <div className="mt-1">{inventario.error.message}</div>
            {inventario.error instanceof ErrorDeApi && inventario.error.detalles?.length && (
              <ul className="mt-1 list-disc pl-5 text-xs text-apagado">
                {inventario.error.detalles.map(d => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {inventario.data && <BarraDeTotales totales={inventario.data.totales} />}

        {permisos.editar && (
          <div className="-mb-2 flex flex-wrap items-center justify-end gap-1.5 text-xs">
            <OpcionesDeBigBags />
            <button
              type="button"
              onClick={() => setEdicionRapida(v => !v)}
              aria-pressed={edicionRapida}
              title={tr(
                'Edita los datos directamente en la tabla: clic en la celda, escribe y Enter. Esc cancela.',
                'Edit the data directly in the table: click the cell, type and press Enter. Esc cancels.',
              )}
              className={`mr-auto rounded-md border px-2 py-0.5 ${
                edicionRapida ? 'border-verde bg-verde-claro text-verde-oscuro' : 'border-linea bg-white hover:bg-papel'
              }`}
            >
              {tr('✎ Edición rápida', '✎ Quick edit')} {edicionRapida ? tr('activa', 'on') : ''}
            </button>
            {seleccion.size > 0 && (
              <button type="button" onClick={() => setSeleccion(new Map())} className="text-apagado hover:text-tinta">
                {tr('Quitar selección', 'Clear selection')}
              </button>
            )}
            <CambioEnLoteDeBigBags bolsas={[...seleccion.values()]} onListo={() => setSeleccion(new Map())} />
            <CambioDeExportacion bolsas={[...seleccion.values()]} onListo={() => setSeleccion(new Map())} />
            {permisos.administrar && (
              <>
                <span className="ml-2 text-apagado">🗑 {tr('Eliminar:', 'Delete:')}</span>
                <BotonDeEliminar
                  etiqueta={`${tr('Seleccionados', 'Selected')}${seleccion.size ? ` (${numero(seleccion.size)})` : ''}`}
                  titulo={tr('Eliminar Big Bags', 'Delete Big Bags')}
                  deshabilitado={!seleccion.size}
                  revisar={async () =>
                    revisionDeBigBags(await eliminar.mutateAsync({modo: 'revisar', codigos: [...seleccion.values()]}))
                  }
                  eliminar={async () =>
                    mensajeDeBigBags(await eliminar.mutateAsync({modo: 'eliminar', codigos: [...seleccion.values()]}))
                  }
                  onTerminado={() => setSeleccion(new Map())}
                />
                <BotonDeEliminar
                  etiqueta={`${tr('Todos los filtrados', 'All filtered')}${inventario.data ? ` (${numero(inventario.data.total)})` : ''}`}
                  titulo={tr('Eliminar todos los Big Bags filtrados', 'Delete all filtered Big Bags')}
                  deshabilitado={!inventario.data?.total}
                  revisar={async () =>
                    revisionDeBigBags(await eliminar.mutateAsync({modo: 'revisar', filtros: soloFiltros(consulta)}))
                  }
                  eliminar={async () =>
                    mensajeDeBigBags(await eliminar.mutateAsync({modo: 'eliminar', filtros: soloFiltros(consulta)}))
                  }
                  onTerminado={() => setSeleccion(new Map())}
                />
              </>
            )}
          </div>
        )}

        <TablaInventario
          filas={inventario.data?.elementos ?? []}
          columnasVisibles={columnas}
          orden={consulta.orden}
          direccion={consulta.direccion}
          onOrdenar={ordenar}
          onReordenar={cambiarColumnas}
          cargando={inventario.isFetching}
          numeracionDesde={(consulta.pagina - 1) * consulta.porPagina}
          seleccion={
            permisos.editar ? {seleccionada: fila => seleccion.has(fila.id), onAlternar: alternarSeleccion} : undefined
          }
          edicion={
            permisos.editar && edicionRapida
              ? {puedeEditar: fila => fila.estado !== 'exported' || permisos.administrar}
              : undefined
          }
        />
        {permisos.editar && edicionRapida && (
          <p className="-mt-2 text-xs text-apagado">
            {tr(
              'Edición rápida: clic en una celda con borde punteado, escribe y Enter guarda; Esc cancela. Los exportados solo los cambia un administrador. Cada cambio queda en el historial de la bolsa y en la bitácora.',
              'Quick edit: click a cell with a dotted border, type and Enter saves; Esc cancels. Exported bags can only be changed by an administrator. Every change is kept in the bag history and the audit log.',
            )}
          </p>
        )}

        <Paginacion
          pagina={consulta.pagina}
          totalPaginas={inventario.data?.totalPaginas ?? 0}
          porPagina={consulta.porPagina}
          total={inventario.data?.total ?? 0}
          onPagina={pagina => cambiar({pagina}, true)}
          onPorPagina={porPagina => cambiar({porPagina})}
        />
      </ZonaAmpliable>
    </section>
  );
}

// Totales de todo lo que cumple el filtro, no solo de la pagina que se ve.
function BarraDeTotales({totales}: {totales: TotalesDeBigBags}) {
  const datos: Array<[string, string, string?]> = [
    ['Big Bags', numero(totales.bigBags)],
    [tr('Ton. húmedas', 'Wet tons'), numero(totales.tonHumedas, 3)],
    [tr('Ton. secas', 'Dry tons'), numero(totales.tonSecas, 3)],
    [tr('Humedad', 'Moisture'), `${numero(totales.humedadTon, 3)} t`, `${numero(totales.humedadPorcentaje, 2)} %`],
    ['Au (g)', numero(totales.auG, 2)],
    ['Ag (g)', numero(totales.agG, 2)],
    ['Au (oz)', numero(totales.auOz, 3)],
    ['Ag (oz)', numero(totales.agOz, 3)],
    ...chipsDeTenor(totales),
  ];
  return <ChipsDeTotales datos={datos} prueba="totales-inventario" />;
}
